// =============================================================================
// domain/leakPlan.ts — two more leaks, both read from what the trader logged.
// -----------------------------------------------------------------------------
//   ADDING TO LOSERS               averaging down, read from the fills
//   CUTTING WINNERS SHORT OF PLAN  your own target vs what you took
//
// Neither needs excursion data or a price feed. Both are grounded in something
// the trader supplied — their fills, and their stated plan — which is what makes
// them arguable at all. See `leakSequence.ts` for the rate/total split every leak
// obeys.
//
// ── 🚨 THE PLAN LEAK IS AN UPPER BOUND, AND THE REPORT MUST SAY SO ──
//
// "You left $5,000 against your own targets" is measured against a target the
// trader SET, not one the market necessarily offered. Without excursion data
// there is no way to know the target was reachable. That is why it is an
// `opportunity` leak rather than a realized one: it can never be ranked against
// dollars actually lost, and any rule written from it has to clear the
// expectancy guardrail before it counts as an improvement.
// =============================================================================

import type { Execution } from './capture';
import type { LeakDollars, LeakRate } from './leakSequence';
import { median } from './leakSequence';

// ---------------------------------------------------------------------------
// Adding to losers
// ---------------------------------------------------------------------------

export interface AddToLoserTrade {
  side: string | null;
  timeIn: string | null;
  netPnl: number | null;
  executions?: readonly Execution[] | null;
}

/** `"HH:MM[:SS]"` → seconds since midnight. */
function clockSeconds(clock: string | null | undefined): number | null {
  if (!clock) return null;
  const m = /^(\d{1,2}):(\d{2})(?::(\d{2}))?/.exec(clock);
  if (!m) return null;
  const h = Number(m[1]); const min = Number(m[2]); const s = Number(m[3] ?? 0);
  if (!Number.isFinite(h) || !Number.isFinite(min)) return null;
  return h * 3600 + min * 60 + s;
}

/** Does this fill open the position, or close it? */
function isBuy(side: string | null | undefined): boolean | null {
  const s = String(side ?? '').toLowerCase();
  if (s === 'buy' || s === 'b' || s === 'long') return true;
  if (s === 'sell' || s === 's' || s === 'short') return false;
  return null;
}

/**
 * Did this trade average down?
 *
 * ⚠ THREE OUTCOMES, AND `null` IS NOT `false`.
 *
 *   true   — an entry-direction fill landed at a price worse than the running
 *            average: below it when long, above it when short.
 *   false  — assessable, and it did not happen.
 *   null   — NOT ASSESSABLE. No fills stored, or no readable side.
 *
 * The distinction is the whole data gate. A trade with no fill detail must not
 * count as "did not average down", because that would quietly dilute the rate
 * with every hand-entered trade the trader ever logged and make the leak look
 * smaller the less data there is.
 *
 * ⚠ ONE ENTRY FILL IS `false`, NOT `null`. It is fully assessable — you cannot
 * add to a position you only entered once.
 */
export function addedToLoser(t: AddToLoserTrade): boolean | null {
  const opensLong = t?.side === 'long';
  if (t?.side !== 'long' && t?.side !== 'short') return null;

  const raw = Array.isArray(t.executions) ? t.executions : null;
  if (!raw || !raw.length) return null;

  const t0 = clockSeconds(t.timeIn);
  const entries: { sec: number | null; price: number; qty: number; i: number }[] = [];

  raw.forEach((e, i) => {
    if (!e) return;
    const buy = isBuy(e.side);
    if (buy === null) return;
    // ⚠ Exit fills are ignored, not counted as adds. A long's SELL is the way
    // out; reading it as an entry would call every scale-out an average-down.
    if (buy !== opensLong) return;

    const price = Number(e.price);
    const qty = Math.abs(Number(e.qty));
    // ⚠ `price > 0`, NOT merely finite — a DELIBERATE divergence from the old
    // client, which checks `isFinite` alone. `Number(null)` is 0 and 0 is
    // finite, so a fill row with a missing price sails through as a price of
    // ZERO — and zero is below any running average, which makes every long
    // carrying one read as an instant average-down. Futures prices are positive;
    // a non-positive one is a broken row, not a fill.
    if (!(price > 0) || !(qty > 0)) return;

    const abs = clockSeconds(e.time);
    let sec = (abs === null || t0 === null) ? null : abs - t0;
    // ⚠ Same midnight roll as a hold time — a fill clock past midnight is later,
    // not twenty-three hours earlier.
    if (sec !== null && sec < 0) sec += 86_400;
    entries.push({ sec, price, qty, i });
  });

  if (entries.length < 2) return false;

  // ⚠ Sorted by time, falling back to the STORED ORDER when a fill has no clock.
  // Comparing against a running average only means anything in the order the
  // fills actually happened.
  entries.sort((a, b) => ((a.sec === null || b.sec === null)
    ? a.i - b.i
    : (a.sec - b.sec) || (a.i - b.i)));

  let avg = (entries[0] as { price: number }).price;
  let q = (entries[0] as { qty: number }).qty;

  for (let k = 1; k < entries.length; k++) {
    const f = entries[k] as { price: number; qty: number };
    if (opensLong ? f.price < avg : f.price > avg) return true;
    avg = (avg * q + f.price * f.qty) / (q + f.qty);
    q += f.qty;
  }
  return false;
}

export interface AddToLoserStats<T extends AddToLoserTrade = AddToLoserTrade> {
  /** Trades with enough fill detail to judge. The rate's denominator. */
  eligible: number;
  addedN: number;
  lostDollars: number;
  lostN: number;
  lostTrades: T[];
}

/** ⚠ Generic on the row — see `tiltContributors` for why the subset keeps its type. */
export function addToLoserStats<T extends AddToLoserTrade>(
  trades: readonly T[],
): AddToLoserStats<T> {
  let eligible = 0; let addedN = 0; let lostDollars = 0; let lostN = 0;
  const lostTrades: T[] = [];

  for (const t of trades) {
    const r = addedToLoser(t);
    if (r === null) continue;
    eligible += 1;
    if (!r) continue;
    addedN += 1;
    const p = Number(t.netPnl) || 0;
    // ⚠ The DOLLAR face is the booked loss on averaged-down trades only. An
    // average-down that worked out cost nothing and is not a leak to fix.
    if (p < 0) { lostDollars += Math.abs(p); lostN += 1; lostTrades.push(t); }
  }
  return { eligible, addedN, lostDollars, lostN, lostTrades };
}

/** The rate: what share of assessable trades averaged down. ⚠ Higher is worse. */
export function addToLoserRate(trades: readonly AddToLoserTrade[]): LeakRate {
  const r = addToLoserStats(trades);
  return {
    value: r.eligible ? (r.addedN / r.eligible) * 100 : null,
    n: r.eligible,
  };
}

export function addToLoserDollars(trades: readonly AddToLoserTrade[]): LeakDollars {
  const r = addToLoserStats(trades);
  return { dollars: r.lostDollars, n: r.lostN, population: r.eligible };
}

// ---------------------------------------------------------------------------
// Cutting winners short of plan
// ---------------------------------------------------------------------------

export interface PlanTrade {
  netPnl: number | null;
  points: number | null;
  qty: number | null;
  rrPlanned: number | null;
  rrRealized: number | null;
  riskPoints: number | null;
  riskAtEntry: number | null;
  /** Dollars per point for the contract. Null when uncatalogued. */
  dollarsPerPoint?: number | null;
}

/**
 * What one R was worth, in dollars.
 *
 * ⚠ THE STORED FIGURE WINS. `riskAtEntry` is what the trader actually risked;
 * rebuilding it from points × $/point × quantity is a reconstruction, and the
 * two disagree whenever the position was scaled. Returns null rather than
 * guessing — a shortfall in R with no dollar value attached is left out of the
 * total instead of being priced wrongly.
 */
export function riskDollars(t: PlanTrade): number | null {
  const stored = Number(t?.riskAtEntry);
  if (stored > 0) return stored;
  const rp = Number(t?.riskPoints);
  const dpp = Number(t?.dollarsPerPoint);
  const qty = Number(t?.qty);
  if (rp > 0 && dpp > 0 && qty > 0) return rp * dpp * qty;
  return null;
}

export interface PlanStats<T extends PlanTrade = PlanTrade> {
  /** Winners with a logged plan. The median's sample. */
  eligible: number;
  /** Median share of the planned reward actually captured, as a percent. */
  capMedian: number | null;
  shortDollars: number;
  shortN: number;
  shortTrades: T[];
}

/** ⚠ Generic on the row — see `tiltContributors` for why the subset keeps its type. */
export function planStats<T extends PlanTrade>(trades: readonly T[]): PlanStats<T> {
  const caps: number[] = [];
  let shortDollars = 0; let shortN = 0; let eligible = 0;
  const shortTrades: T[] = [];

  for (const t of trades) {
    const planned = Number(t?.rrPlanned);
    // ⚠ No planned target means there is nothing to have fallen short OF. These
    // trades leave the population entirely rather than counting as 100%.
    if (!(planned > 0)) continue;

    let realized = (t.rrRealized !== null && Number.isFinite(Number(t.rrRealized)))
      ? Number(t.rrRealized)
      : null;
    if (realized === null) {
      // Derive R from the move and the risk when it was not stored.
      const pts = Number(t.points); const rp = Number(t.riskPoints);
      if (Number.isFinite(pts) && rp > 0) realized = pts / rp;
    }
    // ⚠ WINNERS ONLY. This leak is about cutting a green trade short; a loser
    // did not fall short of its target, it went the other way, and including
    // losers would turn every stop-out into a "shortfall" the size of the plan.
    if (realized === null || realized <= 0) continue;

    eligible += 1;
    // ⚠ CAPPED AT 100. Beating the target is not 140% of a capture — it is a
    // full capture, and letting it exceed 100 would let one runner pull the
    // median above the point where the leak reads as solved.
    caps.push(Math.min(100, (realized / planned) * 100));

    const shortR = Math.max(0, planned - realized);
    if (shortR > 0) {
      const rd = riskDollars(t);
      // ⚠ Priced only when R has a dollar value. Counted in `eligible` either
      // way, so the median stays honest while the total stays conservative.
      if (rd !== null) { shortDollars += shortR * rd; shortN += 1; shortTrades.push(t); }
    }
  }

  return { eligible, capMedian: median(caps), shortDollars, shortN, shortTrades };
}

/** The rate: median capture against plan. ⚠ HIGHER IS BETTER here — 100 is the ceiling. */
export function planShortfallRate(trades: readonly PlanTrade[]): LeakRate {
  const r = planStats(trades);
  return { value: r.capMedian, n: r.eligible };
}

export function planShortfallDollars(trades: readonly PlanTrade[]): LeakDollars {
  const r = planStats(trades);
  return { dollars: r.shortDollars, n: r.shortN, population: r.eligible };
}
